import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useHistory } from 'react-router-dom';
import {
  GenericTable,
  CustomPaginator,
  GenericTableProps,
  SearchAndFilter,
  SearchAndFilterOnChange,
  SearchWithoutScopeProps,
} from '@cdaas/reusable-components-daas';
import {
  InvoicingAccountOptionResponseContents,
  OrganizationResponse,
  UserListUser,
  UserWithUserGroupAndOrganizationResponse,
} from '@cdaas/api-clients/dist/asset-admin-openapi';

import { assetAdminInvoicingAccountClient, assetAdminOrganizationClient, assetAdminUserClient } from '../../../api-client/asset-admin-client';

import InviteUser from './InviteUser';

import styles from './UserList.module.scss';
import { Button, Div, GrowlNotification, Gutter } from '@gaiads/telia-react-component-library';

interface UsersParamTypes {
  id: string;
}

const pageSize = 10;
const USER_MAX_LIMIT = 32000; // Use limit to get all users

enum SortableColumns {
  Name,
  UserGroup,
  Status,
}

export default function Users() {
  const { t } = useTranslation('organizationForm');
  const { t: tError } = useTranslation('errorCodes');

  const organizationId = useParams<UsersParamTypes>().id;

  const [loadingUsers, setLoadingUsers] = useState<GenericTableProps<UserListUser>['loading']>('short');
  const [loadingOrganization, setLoadingOrganization] = useState(true);
  const [loadingInvoicingAccounts, setLoadingInvoicingAccounts] = useState(true);
  const history = useHistory();

  const [invoicingAccounts, setInvoicingAccounts] = useState<InvoicingAccountOptionResponseContents[]>([]);
  const [users, setUsers] = useState<UserWithUserGroupAndOrganizationResponse[]>([]);
  const [usersPage, setUsersPage] = useState<UserWithUserGroupAndOrganizationResponse[] | undefined>();
  const [organization, setOrganization] = useState<OrganizationResponse>();

  const [fetchError, setFetchError] = useState(false);
  const [organizationError, setOrganizationError] = useState(false);
  const [invoicingAccountsError, setInvoicingAccountsError] = useState(false);
  const [inviteUserModalVisible, setInviteUserModalVisible] = useState(false);

  const [currentSort, setCurrentSort] = useState<SortableColumns>();

  const [searchText, setSearchText] = useState<string>('');

  const handlePageChange = (pageItems: UserWithUserGroupAndOrganizationResponse[]) => {
    setUsersPage(pageItems);
  };

  const fetchUsers = async (organizationId: string, search?: string) => {
    setLoadingUsers('short');
    try {
      const { data } = await assetAdminUserClient.adminListOrganizationUsers(organizationId, search, undefined, 0, USER_MAX_LIMIT);
      setUsers(data.contents);
    } catch {
      setFetchError(true);
    } finally {
      setLoadingUsers('none');
    }
  };

  const fetchOrganization = async (organizationId: string) => {
    setOrganizationError(false);
    setLoadingOrganization(true);
    try {
      const { data } = await assetAdminOrganizationClient.adminOrganizationRead(organizationId, 'user_groups');
      if (data) {
        setOrganization(data);
      } else {
        setOrganizationError(true);
      }
    } catch {
      setOrganizationError(true);
    } finally {
      setLoadingOrganization(false);
    }
  };

  const fetchInvoicingAccounts = async (organizationId: string) => {
    setInvoicingAccountsError(false);
    setLoadingInvoicingAccounts(true);
    try {
      const { data } = await assetAdminInvoicingAccountClient.adminReadInvoicingAccountOptions(organizationId, 99999);
      setInvoicingAccounts(data.contents);
    } catch {
      setInvoicingAccountsError(true);
    } finally {
      setLoadingInvoicingAccounts(false);
    }
  };

  useEffect(() => {
    fetchOrganization(organizationId);
    fetchInvoicingAccounts(organizationId);
  }, [organizationId]);

  useEffect(() => {
    fetchUsers(organizationId, searchText);
  }, [organizationId, searchText]);

  useEffect(() => {
    setUsersPage(users.slice(0, pageSize));
  }, [users]);

  const handleSortChange = (sortColumn: SortableColumns) => {
    const sortedUsers = [...users];
    if (sortColumn === currentSort) {
      sortedUsers.reverse();
    } else {
      if (sortColumn === SortableColumns.Name) {
        sortedUsers.sort((a, b) => {
          const aNameOrEmail = (a.name || a.email).toLowerCase();
          const bNameOrEmail = (b.name || b.email).toLowerCase();
          return aNameOrEmail < bNameOrEmail ? -1 : 1;
        });
      } else if (sortColumn === SortableColumns.UserGroup) {
        sortedUsers.sort((a, b) =>
          a.user_group.name && b.user_group.name && a.user_group.name.toLowerCase() < b.user_group.name.toLowerCase() ? -1 : 1,
        );
      } else if (sortColumn === SortableColumns.Status) {
        sortedUsers.sort((a, b) => {
          const aActive = a.given_name && a.family_name;
          const bActive = b.given_name && b.family_name;
          return aActive || !bActive ? -1 : 1;
        });
      }
    }
    setCurrentSort(sortColumn);
    setUsers(sortedUsers);
    const currentPageUsers = sortedUsers.slice(0, pageSize);
    handlePageChange(currentPageUsers);
  };

  const content = (
    <div className={styles['users__users-table-container']}>
      <GenericTable
        loading={loadingUsers}
        errorText={fetchError ? t('users.fetchFailedErrorMessage') : undefined}
        noDataText={t('users.noUsersMessage')}
        inputData={usersPage}
        rowConfigs={[
          {
            header: t('users.headers.name'),
            formatValue: (_value, row) => row.name || row.email,
            fullyExternalSortFunction: () => handleSortChange(SortableColumns.Name),
            cellWidthMultiplier: 2,
          },
          {
            header: t('users.headers.userGroup'),
            key: 'user_group.name',
            formatValue: value => value,
            fullyExternalSortFunction: () => handleSortChange(SortableColumns.UserGroup),
            hideAtBreakpoint: 'xs',
          },
          {
            header: t('users.headers.status'),
            formatValue: (_value, row) => String(row.given_name && row.family_name ? t('users.activeUser') : t('users.inactiveUser')),
            fullyExternalSortFunction: () => handleSortChange(SortableColumns.Status),
            hideAtBreakpoint: 'md',
          },
        ]}
        buttonConfig={{
          name: t('users.showUserButton'),
          buttonProps: {
            variant: 'secondary',
            iconName: 'chevron-right',
            size: 'md',
            iconOnRight: true,
          },
          buttonClickCallback: (user: UserWithUserGroupAndOrganizationResponse) =>
            history.push(`usergroups/${user.user_group?.id}/users/${user.id}`, {
              clickedFromPathname: history.location.pathname,
              clickedFromPage: 'users',
            }),
        }}
      />
      <CustomPaginator
        activateInputData={{
          inputData: users,
          pageSize,
        }}
        onPageChange={pageChangeInfo => handlePageChange(pageChangeInfo.nextPageItems)}
      />
    </div>
  );

  const handleCloseModal = (updated: boolean) => {
    setInviteUserModalVisible(false);
    if (updated) {
      fetchUsers(organizationId);
    }
  };

  const handleSearchChange = (values: SearchAndFilterOnChange) => {
    const newSearchText = values.search?.value ?? '';
    setSearchText(newSearchText);
  };

  return (
    <>
      <Button
        iconName="add"
        iconOnRight={true}
        variant={'secondary'}
        disabled={loadingUsers !== 'none' || loadingOrganization || loadingInvoicingAccounts}
        onClick={() => setInviteUserModalVisible(true)}
      >
        {t('inviteUserModal.inviteUserButton')}
      </Button>
      <Gutter size={'lg'} />
      <Div className={styles['users__users-search-container']}>
        {searchText !== undefined && (
          <SearchAndFilter
            onChangeCallback={handleSearchChange}
            searchConfig={{
              useDebounceInTextSearch: 1000,
              searchInputOrSearchInputWithScopeProps: {
                placeholder: t('usersAdminList.searchUsersPlaceholder'),
                title: t('usersAdminList.searchUsersTitle'),
                forceUpdateValue: searchText,
              } as SearchWithoutScopeProps,
            }}
          />
        )}
      </Div>
      {content}
      {inviteUserModalVisible && organization && (
        <InviteUser invoicingAccounts={invoicingAccounts} organization={organization} closeModal={handleCloseModal} />
      )}
      {organizationError && (
        <GrowlNotification notification={tError('organization.fetchFailed')} variant={'error'} closeOnClick={() => setOrganizationError(false)} />
      )}
      {invoicingAccountsError && (
        <GrowlNotification
          notification={tError('invoicingAccount.fetchFailed')}
          variant={'error'}
          closeOnClick={() => setInvoicingAccountsError(false)}
        />
      )}
    </>
  );
}
